import { useCallback, useEffect, useRef, useState } from 'react';
import { resolveLinkedId } from '../../../stores/claim-editor/claim-editor-linked-ids';

/** A reference id together with the identity key of the CANONICAL record it points at. */
export interface VerifiedLink {
    id: string;
    key: string;
}

/**
 * Tracks which record a form's reference id stands for, and only trusts an id that has
 * been checked against that record.
 *
 * A loaded claim cannot vouch for itself: a stale id sitting beside edited fields is the
 * exact corrupted state `resolveLinkedId` exists to catch, and a baseline keyed from the
 * claim's own fields would bless it. So the baseline is built from the record fetched BY
 * that id, and if that fetch fails or finds nothing the id is simply not sent and the
 * backend's create-or-lookup-by-fields path takes over.
 *
 * The link lives in a ref, not in form values: it must survive Formik reinitialising and
 * must be readable from the debounced save, which closes over its first render.
 *
 * COORDINATION WITH THE FORM'S AUTO-SAVE. While the fetch is in flight `resolveId` can only
 * answer "no id", so a save in that window would write an ID-less record back to the store,
 * change `id`, and orphan the very request that was about to verify it. Forms therefore:
 *   - skip persisting while `isPending()` is true, and
 *   - persist again when `settled` changes, which is when the answer is known.
 * A request that FAILS (as opposed to finding no record) stays pending and blocks persistence;
 * see `verify`. Every explicit `setLink` (a lookup pick or clear) bumps a generation, so a verification
 * that was already in flight can never overwrite that newer decision.
 */
/** Waits between attempts after a failed verification request; one more attempt than delays. */
const RETRY_DELAYS_MS = [1000, 3000, 9000];

export function useVerifiedLink<R>(
    id: string | null | undefined,
    fetchRecord: (id: string) => Promise<R | null | undefined>,
    keyOfRecord: (record: R) => string,
) {
    const linkRef = useRef<VerifiedLink | null>(null);
    /** An id whose verification is over and did not produce a link — do not fetch it again. */
    const unverifiedIdRef = useRef<string | null>(null);
    const generationRef = useRef(0);
    const [, rerender] = useState(0);
    const [settled, setSettled] = useState(0);
    const [failedId, setFailedId] = useState<string | null>(null);

    // Written during render so callers running before this hook's effect still see it.
    const latestIdRef = useRef('');
    latestIdRef.current = id || '';

    // Keep the latest callbacks reachable without making them effect dependencies.
    const fetchRef = useRef(fetchRecord);
    const keyRef = useRef(keyOfRecord);
    fetchRef.current = fetchRecord;
    keyRef.current = keyOfRecord;

    /** An explicit decision: a lookup pick (`link`) or clear (`null`). Supersedes any fetch in flight. */
    const setLink = useCallback((link: VerifiedLink | null) => {
        generationRef.current += 1;
        linkRef.current = link;
        unverifiedIdRef.current = link ? null : latestIdRef.current;
        setFailedId(null);
        rerender(n => n + 1);
    }, []);

    /**
     * Fetches the canonical record for `verifyId` and settles the link.
     *
     * "Not found" (the fetcher returns null) and "could not ask" (it throws) are different
     * answers. Not found rules the id out — it is dropped. A failed request rules NOTHING out:
     * a temporary outage must not rewrite a loaded claim without its id, which would create a
     * duplicate record by fields. So a failure leaves the id pending (persistence stays
     * blocked), retries a few times, then reports `verificationFailed` for the form to surface;
     * `retry` asks again.
     */
    const verify = useCallback(async (verifyId: string) => {
        const generation = (generationRef.current += 1);
        const superseded = () => generation !== generationRef.current;

        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
            try {
                const record = await fetchRef.current(verifyId);
                if (superseded()) return; // a newer pick, clear or id change won
                linkRef.current = record ? { id: verifyId, key: keyRef.current(record) } : null;
                unverifiedIdRef.current = record ? null : verifyId;
                setFailedId(null);
                setSettled(n => n + 1);
                return;
            } catch {
                if (superseded()) return;
                if (attempt === RETRY_DELAYS_MS.length) break;
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
                if (superseded()) return;
            }
        }
        setFailedId(verifyId);
    }, []);

    useEffect(() => {
        if (!id) {
            unverifiedIdRef.current = null;
            if (linkRef.current) {
                linkRef.current = null;
                rerender(n => n + 1);
            }
            return;
        }
        if (linkRef.current?.id === id || unverifiedIdRef.current === id) return;

        // Unverified until the canonical record answers.
        linkRef.current = null;
        setFailedId(null);
        void verify(id);
    }, [id, verify]);

    /** Ask again after a failed verification. */
    const retry = useCallback(() => {
        if (!latestIdRef.current) return;
        setFailedId(null);
        void verify(latestIdRef.current);
    }, [verify]);

    /** True while an id is present but has neither been verified nor ruled out. */
    const isPending = useCallback(
        () =>
            !!latestIdRef.current &&
            linkRef.current === null &&
            unverifiedIdRef.current !== latestIdRef.current,
        [],
    );

    /** The id to send: `id` only while it is verified AND the form still matches that record. */
    const resolveId = useCallback(
        (candidate: string | null | undefined, currentKey: string): string | undefined =>
            resolveLinkedId(
                candidate,
                candidate && linkRef.current?.id === candidate ? linkRef.current.key : undefined,
                currentKey,
            ),
        [],
    );

    /** For forms that hold no id in their values: the linked id while the form still matches its record. */
    const resolveLinked = useCallback(
        (currentKey: string): string | undefined =>
            linkRef.current && linkRef.current.key === currentKey ? linkRef.current.id : undefined,
        [],
    );

    const verificationFailed = !!failedId && failedId === (id || '');

    return { setLink, resolveId, resolveLinked, isPending, settled, verificationFailed, retry };
}
